/**
 * Linked practitioners to offer while booking a forløb.
 *
 * Tilknytninger include every staff member on the patient. Suggestions keep
 * only people whose role has the Behandler flag, and only when they are in
 * the selectable set for this step. The idle list then mirrors Værktøjer →
 * Spørgeskemaer: a short labelled group (like «Tilladte på dette trin»),
 * with search reaching the rest of the selectable practitioners.
 */

export type PractitionerPickRow = {
  id: string;
  name: string;
};

export function suggestedLinkedPractitionerIds(input: {
  assignmentUserIds: readonly string[];
  /** User ids whose role has the Behandler flag. */
  behandlerIds: ReadonlySet<string>;
  /**
   * Step-eligible ids. Null means the step does not restrict the set, so
   * every flagged behandler may be suggested.
   */
  selectableIds?: ReadonlySet<string> | null;
}): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of input.assignmentUserIds) {
    if (!id || seen.has(id)) continue;
    if (!input.behandlerIds.has(id)) continue;
    if (input.selectableIds && !input.selectableIds.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * One linked behandler is selected for the slot search. Several stay as
 * suggestions until the user picks. Null leaves the current selection alone.
 */
export function practitionerSelectionAfterSuggestion(input: {
  currentIds: readonly string[];
  suggestedIds: readonly string[];
  userAdjusted: boolean;
}): string[] | null {
  if (input.userAdjusted || input.currentIds.length > 0) return null;
  if (input.suggestedIds.length !== 1) return null;
  return [input.suggestedIds[0]];
}

/**
 * Idle: suggested rows, then anyone already selected who is not in that
 * group (the same way a picked questionnaire stays after «Tilladte»).
 * A query searches the whole selectable set and hides the suggestion label.
 *
 * Bloker tid sets `showEveryoneWhenIdle` so an empty search lists the whole
 * roster with selected people first. A search still filters, and matching
 * selections stay above the other hits. Callers that leave the flag off keep
 * the suggestion group. Forløb booking uses `practitionerTypeaheadRows`.
 */
export function practitionerPickRows(input: {
  query: string;
  people: readonly PractitionerPickRow[];
  suggestedIds: readonly string[];
  selectedIds: readonly string[];
  showEveryoneWhenIdle?: boolean;
}): { rows: PractitionerPickRow[]; showSuggestions: boolean } {
  const byId = new Map<string, PractitionerPickRow>();
  for (const person of input.people) {
    if (!person.id || byId.has(person.id)) continue;
    byId.set(person.id, person);
  }

  const query = input.query.trim().toLowerCase();
  if (query) {
    const matches = [...byId.values()]
      .filter((person) => person.name.toLowerCase().includes(query))
      .sort((a, b) => a.name.localeCompare(b.name, 'da'));
    return {
      showSuggestions: false,
      rows: input.showEveryoneWhenIdle ? selectedFirst(matches, input.selectedIds) : matches,
    };
  }

  if (input.showEveryoneWhenIdle) {
    return {
      showSuggestions: false,
      rows: selectedFirst([...byId.values()], input.selectedIds),
    };
  }

  const rows: PractitionerPickRow[] = [];
  const seen = new Set<string>();
  for (const id of input.suggestedIds) {
    const person = byId.get(id);
    if (!person || seen.has(id)) continue;
    seen.add(id);
    rows.push(person);
  }
  const suggestedCount = rows.length;
  for (const id of input.selectedIds) {
    const person = byId.get(id);
    if (!person || seen.has(id)) continue;
    seen.add(id);
    rows.push(person);
  }
  return { rows, showSuggestions: suggestedCount > 0 };
}

/**
 * Forløb Vælg tid and Ny aftale. Matches open under the field, like the
 * patient search. Selected people stay pinned above those matches so a long
 * list cannot hide them. Soft favorites (patient tilknytninger) sit above the
 * other matches and are never selected by this list.
 */
export function practitionerTypeaheadRows(input: {
  query: string;
  people: readonly PractitionerPickRow[];
  selectedIds: readonly string[];
  favoriteIds?: readonly string[];
}): {
  pinned: PractitionerPickRow[];
  favorites: PractitionerPickRow[];
  matches: PractitionerPickRow[];
} {
  const byId = new Map<string, PractitionerPickRow>();
  for (const person of input.people) {
    if (!person.id || byId.has(person.id)) continue;
    byId.set(person.id, person);
  }

  const selected = new Set(input.selectedIds);
  const favoriteSet = new Set(input.favoriteIds ?? []);
  const query = input.query.trim().toLowerCase();
  const pinned: PractitionerPickRow[] = [];
  const favoriteBucket = new Map<string, PractitionerPickRow>();
  const matches: PractitionerPickRow[] = [];
  for (const person of byId.values()) {
    if (selected.has(person.id)) {
      pinned.push(person);
      continue;
    }
    if (query && !person.name.toLowerCase().includes(query)) continue;
    if (favoriteSet.has(person.id)) favoriteBucket.set(person.id, person);
    else matches.push(person);
  }
  pinned.sort(byDanishName);
  matches.sort(byDanishName);

  const favorites: PractitionerPickRow[] = [];
  const seenFavorites = new Set<string>();
  for (const id of input.favoriteIds ?? []) {
    const person = favoriteBucket.get(id);
    if (!person || seenFavorites.has(id)) continue;
    seenFavorites.add(id);
    favorites.push(person);
  }
  return { pinned, favorites, matches };
}

function byDanishName(a: PractitionerPickRow, b: PractitionerPickRow): number {
  return a.name.localeCompare(b.name, 'da');
}

/** Keep each group's existing order. Selected rows move above the rest. */
function selectedFirst(
  rows: readonly PractitionerPickRow[],
  selectedIds: readonly string[],
): PractitionerPickRow[] {
  const selected = new Set(selectedIds);
  const picked: PractitionerPickRow[] = [];
  const rest: PractitionerPickRow[] = [];
  for (const row of rows) {
    if (selected.has(row.id)) picked.push(row);
    else rest.push(row);
  }
  return [...picked, ...rest];
}
